import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, errMsg } from "../lib/api";
import type { ApplianceState } from "../lib/types";
import { Button, Spinner } from "../components/ui";

const ACTION_TO_STATE: Record<string, ApplianceState> = {
  start: "RUNNING",
  done: "DONE",
  reset: "IDLE",
};

type ErrorKind = "unauthenticated" | "general";

export function NfcPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const device = params.get("device");
    const action = (params.get("action") ?? "tap").toLowerCase();
    // A bare tag (no action, or the legacy "start") is a smart tap: the server
    // decides claim vs. start vs. unload by appliance state + haManaged.
    // "done"/"reset" stay explicit state events for manual tags.
    const isTap = action === "tap" || action === "start";
    const state = isTap ? null : ACTION_TO_STATE[action];

    if (!device || (!isTap && !state)) {
      setErrorKind("general");
      setErrorMsg("Missing or invalid NFC parameters.");
      return;
    }

    (async () => {
      try {
        if (isTap) {
          await api.post(`/appliance/${device}/tap`, {});
        } else {
          await api.post(`/appliance/${device}/event`, { state, source: "NFC" });
        }
        navigate("/", { replace: true });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setErrorKind("unauthenticated");
        } else {
          setErrorKind("general");
          setErrorMsg(errMsg(err, "Couldn't update appliance."));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="text-center">
        {errorKind === "unauthenticated" ? (
          <>
            <h2 className="text-base font-semibold text-slate-900">Sign in first</h2>
            <p className="mt-1 max-w-xs text-sm text-slate-500">
              Sign in to your account, then tap the tag again.
            </p>
            <Button className="mt-4" onClick={() => navigate("/login", { replace: true })}>
              Sign in
            </Button>
          </>
        ) : errorKind === "general" ? (
          <>
            <h2 className="text-base font-semibold text-rose-700">Tap failed</h2>
            <p className="mt-1 max-w-sm text-sm text-slate-600">{errorMsg}</p>
            <Button variant="secondary" className="mt-4" onClick={() => navigate("/", { replace: true })}>
              Go home
            </Button>
          </>
        ) : (
          <>
            <Spinner />
            <p className="mt-3 text-sm text-slate-500">Updating appliance…</p>
          </>
        )}
      </div>
    </div>
  );
}
